import React, { useId } from 'react';
import { Box, Icon, InputField, Typography } from '@saviynt/design-system';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { sortedList } from './utils';

import './Dropdown.css';

function Dropdown({
  headerLabel,
  placeholder,
  searchLabel,
  isHeaderRequired,
  options,
  searchText,
  setSearchText,
  handleChange,
  showSearchBar,
}) {
  const inputId = useId();
  const selectedOption = options?.find((item) => item.isChecked === true);

  return (
    <Box className='Dropdown'>
      {isHeaderRequired && (
        <Typography
          kind='body3'
          desktop='body3-d'
          mobile='body3-d'
          color='--color-foreground-neutral-subtle'
          className='Dropdown-header'>
          {headerLabel}
        </Typography>
      )}
      {showSearchBar && (
        <Box className='Dropdown-search'>
          <InputField
            id={inputId}
            name='sort-filter-search'
            placeholder={placeholder}
            label={
              <Typography kind='label' htmlFor={inputId}>
                {searchLabel || headerLabel || placeholder}
              </Typography>
            }
            kind='search'
            size='small'
            prefixIcon={
              <Icon kind='search' color='neutral-700' size='smallMedium' />
            }
            value={searchText}
            setValue={setSearchText}
            tabIndex={0}
          />
        </Box>
      )}
      <div
        className='Dropdown-menuList'
        role='listbox'
        aria-label={headerLabel || placeholder}>
        {sortedList(options, selectedOption).map((ele) => {
          const isChecked = selectedOption?.value === ele.value;
          const menulistClassNames = classNames(
            'DropdownItem',
            isChecked && 'DropdownItem--isChecked'
          );

          return (
            <div
              key={ele.value}
              className={menulistClassNames}
              role='option'
              aria-selected={isChecked}
              tabIndex={0}
              onClick={() => {
                if (handleChange) handleChange(ele);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && handleChange) {
                  handleChange(ele);
                }
              }}>
              <Typography
                kind={isChecked ? 'body2-bold' : 'body2'}
                color='--color-foreground-secondary-bold'>
                {ele.label}
              </Typography>
              {!selectedOption && (
                <Icon kind='ChevronRight' color='navy-900' size='small' />
              )}
              {isChecked && (
                <Icon kind='Check' color='navy-900' size='medium' />
              )}
            </div>
          );
        })}
      </div>
    </Box>
  );
}

Dropdown.propTypes = {
  headerLabel: PropTypes.string,
  placeholder: PropTypes.string,
  searchLabel: PropTypes.string,
  isHeaderRequired: PropTypes.bool,
  showSearchBar: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
      isChecked: PropTypes.bool,
      isIconFillDisabled: PropTypes.bool,
      columnName: PropTypes.string,
    })
  ).isRequired,
  searchText: PropTypes.string.isRequired,
  setSearchText: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
};

Dropdown.defaultProps = {
  showSearchBar: true,
  isHeaderRequired: true,
  headerLabel: '',
  placeholder: '',
  searchLabel: '',
};

export default Dropdown;
